import classNames from "classnames";
import { FunctionComponent, ReactElement } from "react";
import CollegeLogo from "rivals/components/shared/CollegeLogo";
import RivalsLogo from "rivals/components/shared/Icons/RivalsLogo";
import Link from "rivals/components/shared/Link";
import {
  getRankArgs,
  inchesToFeet,
  RankInfo,
  RIVALS_RATING_DIGITS
} from "rivals/components/shared/Prospect/utils";
import Stars from "rivals/components/shared/Stars";
import { setData } from "rivals/shared/utils/qa";
import styles from "./EmbedProspectCard.module.scss";
import type { Props } from "./types";

function rankChangeArrow(rankChange: number): ReactElement {
  const arrowClasses = classNames(
    styles.arrowbox,
    { [styles.up]: rankChange > 0 },
    { [styles.down]: rankChange < 0 }
  );
  return <span className={arrowClasses} />;
}

function rankBlock(index: number, rankInfo: RankInfo): ReactElement {
  const { rank, rankChange, rankType, rankUrl } = rankInfo;

  return (
    <div className={styles.rank} key={index}>
      <div className={styles.row}>
        {rankChangeArrow(rankChange)}
        <span className={styles.number}>
          {rankUrl ? (
            <Link href={rankUrl}>{rank}</Link>
          ) : (
            <div> {rank || "-"} </div>
          )}
        </span>
      </div>
      <div className={styles.type}>{rankType}</div>
    </div>
  );
}

const EmbedProspectCard: FunctionComponent<Props> = ({
  prospect
}): ReactElement => {
  const {
    firstName,
    lastName,
    height,
    weight,
    positionAbbreviation,
    highschoolName,
    hometown,
    recruitYear,
    stars,
    rivalsRating,
    committedCollegeLogo,
    committedCollegeName,
    bigCommitLogo,
    profileImageUrl,
    status,
    commitDate
  } = prospect;
  const rankArgs = getRankArgs(prospect, true);

  const collegeLogo = committedCollegeLogo ? (
    <CollegeLogo
      collegeLogo={committedCollegeLogo}
      collegeName={committedCollegeLogo as string}
      dataPhilter="commited-college-logo"
      logoSize={36}
    />
  ) : null;

  const prospectName = (
    <div className={styles.prospectName}>
      <div className={styles.firstName}> {firstName} </div>
      <div className={styles.lastName}> {lastName} </div>
    </div>
  );

  const formattedRating = rivalsRating
    ? rivalsRating.toPrecision(RIVALS_RATING_DIGITS)
    : "N/A";

  const ratingElement = (
    <div className={styles.rating}>
      <div className={styles.decimalRankNumber}>{formattedRating}</div>
      <RivalsLogo fill="white" height="7px" />
      <div className={styles.smallRankText}>RATING</div>
    </div>
  );

  const heightWeightPosClass = (
    <div
      className={styles.prospectHeightWeightPosClass}
      {...setData("prospect-height-weight-pos-class")}
    >
      <span className="height">{inchesToFeet(height)}</span>
      <span className={styles.breakLine}>|</span>
      <span className="weight">{weight} lbs</span>
      <span className={styles.breakLine}>|</span>
      <span className="pos">{positionAbbreviation}</span>
      <span className={styles.breakLine}>|</span>
      <span className="class-of">Class of {recruitYear}</span>
    </div>
  );

  return (
    <div className={styles.container} {...setData("embed-prospect-card")}>
      <div className={styles.backgroundContainer}>
        {bigCommitLogo && (
          <CollegeLogo
            collegeLogo={bigCommitLogo}
            collegeName={committedCollegeLogo as string}
            dataPhilter="background"
            logoSize={300}
          />
        )}
      </div>
      <div className={styles.content}>
        <div className={styles.row}>
          <div className={styles.column}>
            {prospectName}
            {heightWeightPosClass}
          </div>
          <div
            className={styles.starsAndRating}
            {...setData("stars-and-rating")}
          >
            <Stars numStars={stars} />
            {ratingElement}
          </div>
        </div>
        <div className={styles.thickRow}>
          <div className={styles.prospect}>
            <img
              alt="profile image"
              className={styles.prospectImage}
              src={
                profileImageUrl ||
                "/static/icons/icons_prospectprofile_avatar.svg"
              }
              {...setData("prospect-image")}
            />
            <div className={styles.homeAndCommitStatus}>
              <div className={styles.homeInformation}>
                <div> {highschoolName} </div>
                <div className={styles.hometown}> {hometown} </div>
              </div>
              <div
                className={styles.signingBlock}
                {...setData("signing-block")}
              >
                {collegeLogo}
                <span className={styles.commitInfo}>
                  <div className={styles.commitStatus}>{status}</div>
                  <div className={styles.commitDate}>{commitDate}</div>
                  {committedCollegeName}
                </span>
              </div>
            </div>
          </div>
          <div className={styles.rankings}>
            {rankArgs.map((rankInfo, i) => rankBlock(i, rankInfo))}
          </div>
        </div>
      </div>
    </div>
  );
};
export default EmbedProspectCard;
